import {
  Box,
  makeStyles,
  Theme,
  Button,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { useFormikContext } from 'formik';
import React from 'react';
import { SortableElement, SortableElementProps } from 'react-sortable-hoc';
import Activator from '../Activator';
import * as Colors from '../../theme/colors';
import BaseTypography, { FontType } from '../Text/BaseTypography';
import { GraySmall, BlackHeadings } from '../../theme/colors';
import ColorUtils from '../../utils/ColorUtils';
import { SortDragButton } from '../SortDragButton/SortDragButton';
import { Action, BaseActionsMenu } from '../Dropdowns';

/**
 * TODO: set all ModuleSettingCardProps properties types to not optional once
 * sidebar modularity project is removed.
 */
interface ModuleSettingCardV2Props {
  isSortable?: boolean;
  menuActions?: Action[];
  order?: number;
}
interface ModuleSettingCardV1Props extends ModuleSettingCardV2Props {
  fieldName?: string;
  checked?: boolean;
  title: string;
  Icon?: React.ReactNode;
  description: string;
  learnMoreLink?: string;
  disabled?: boolean;
  tooltip?: string;
  hideBorder?: boolean;
  invertBooleanValue?: boolean;
  hideIcon?: boolean;
  titleFontType?: FontType;
  descriptionFontType?: FontType;
  noToggleButton?: boolean;
}

interface StylingProps {
  hideBorder: boolean;
  isSortable: boolean;
}
const useStyles = makeStyles((theme: Theme) => ({
  root: {
    borderBottom: (props: StylingProps) =>
      props.hideBorder ? 'unset' : `1px solid ${Colors.DividersAndCardBorders}`,
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
    '&:first-child': {
      paddingTop: 0,
    },
    '&:last-child': {
      paddingBottom: theme.spacing(1),
    },
    zIndex: (props: StylingProps) =>
      props.isSortable ? theme.zIndex.modal + 1 : 'inherit',
  },
  title: {
    color: BlackHeadings,
  },
  description: {
    color: GraySmall,
  },
  moduleContentRow: {
    display: 'flex',
    alignItems: 'center',
    maxWidth: '70%',
  },
  moduleIconWrapper: {
    backgroundColor: ColorUtils.GetColorDarknessShades(
      theme.palette.primary.main,
    ).light,
    borderRadius: '50%',
    height: 40,
    width: 40,
    minHeight: 40,
    minWidth: 40,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  moduleIcon: {
    height: 20,
    width: 20,
    '& path': {
      strokeWidth: 2,
    },
  },
}));

const SortableItem = SortableElement(
  ({ children }: SortableElementProps & React.PropsWithChildren<any>) =>
    children,
);

const SettingsToggleCard: React.FC<ModuleSettingCardV1Props> = props => {
  const {
    fieldName,
    checked,
    title,
    Icon,
    description,
    learnMoreLink,
    disabled = false,
    tooltip,
    hideBorder = false,
    invertBooleanValue = false,
    children,
    hideIcon = false,
    titleFontType = '15Medium',
    descriptionFontType = '13Regular',
    isSortable,
    order,
    menuActions,
    noToggleButton = false,
  } = props;
  const classes = useStyles({ hideBorder, isSortable: isSortable ?? false });
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const { handleBlur, setFieldValue } = useFormikContext();

  const settingsCardRenderer = () => (
    <Box
      display="flex"
      flexDirection="column"
      className={classes.root}
      width={1}
    >
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        width={1}
      >
        <Box mr={5.5} className={classes.moduleContentRow}>
          {isSortable && !isMobileScreen && (
            <Box mr={2}>
              <SortDragButton style={{ fontSize: '16px' }} />
            </Box>
          )}

          {!hideIcon && Icon && (
            <Box className={classes.moduleIconWrapper} mr={2.5}>
              <Icon className={classes.moduleIcon} />
            </Box>
          )}
          <Box display="flex" flexDirection="column" flexGrow={1}>
            <BaseTypography fontType={titleFontType} className={classes.title}>
              {title}
            </BaseTypography>
            {description && (
              <BaseTypography
                fontType={descriptionFontType}
                className={classes.description}
              >
                {description}
              </BaseTypography>
            )}
            {learnMoreLink && (
              // wrapped in div to prevent the button from being stretched
              <div>
                <Button color="primary" href={learnMoreLink} target="_blank">
                  Learn more
                </Button>
              </div>
            )}
          </Box>
        </Box>
        <Box
          flex={1}
          display="flex"
          justifyContent="flex-end"
          alignItems="center"
        >
          {menuActions && !isMobileScreen && (
            <Box mx={2}>
              <BaseActionsMenu actions={menuActions} />
            </Box>
          )}
        </Box>

        {!noToggleButton && (
          <Activator
            checked={invertBooleanValue ? !checked : checked}
            label=""
            onChange={(_, isChecked) => {
              setFieldValue(
                fieldName,
                invertBooleanValue ? !isChecked : isChecked,
              );
            }}
            onBlur={handleBlur}
            name={fieldName}
            disabled={disabled}
            tooltip={tooltip}
            isFixedWidth={false}
          />
        )}
      </Box>
      <Box width={1} pl={4.5}>
        {children}
      </Box>
    </Box>
  );

  return isSortable && !isMobileScreen ? (
    <SortableItem index={order}>{settingsCardRenderer()}</SortableItem>
  ) : (
    settingsCardRenderer()
  );
};

export default SettingsToggleCard;
